import { observable, action, computed, configure, runInAction } from "mobx";
import { createContext } from "react";
import { toast } from "react-toastify";
import agent from "../api/agent";

configure({ enforceActions: "always" });

class TimeSheetPayStore {
  @observable timesheetallRegistry = new Map();
  @observable timesheetpayrunRegistry = new Map();
  @observable timesheetemployeeRegistry = new Map();
  @observable payrunRegistry = new Map();
  @observable payrollcycleRegistry = new Map();
  @observable employeeRegistry = new Map();
  @observable employeepayrunRegistry = new Map();
  @observable employeestatusRegistry = new Map();
  @observable payrunweekRegistry = new Map();
  @observable projecttimeRegistry = new Map();
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";

  @computed get timesheetsByDate() {
    return Array.from(this.timesheetemployeeRegistry.values()).sort(
      (a, b) => Date.parse(a.attendanceDate) - Date.parse(b.attendanceDate)
    );
  }

  @computed get payrunstatusByDate() {
    return Array.from(this.payrunRegistry.values()).sort(
      (a, b) => a.financeYear - b.financeYear || a.payRun.localeCompare(b.payRun)
    );
  }

  @computed get payrollcyclesByName() {
    return Array.from(this.payrollcycleRegistry.values()).sort(
      (a, b) =>
        a.startYear.localeCompare(b.startYear) ||
        a.endYear.localeCompare(b.endYear) ||
        a.payRun.localeCompare(b.payRun)
    );
  }

  @computed get employeeByName() {
    return Array.from(this.employeepayrunRegistry.values()).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }

  @computed get payrunToCSV() {
    return Array.from(this.timesheetpayrunRegistry.values()).map(
      ({id, createdAt, updatedAt, ...keepAttrs}) => keepAttrs);
  }

  @computed get employeeToCSV() {
    return Array.from(this.timesheetemployeeRegistry.values()).map(
      ({id, createdAt, updatedAt, ...keepAttrs}) => keepAttrs);
  }

  @action loadPayrollCycles = async () => {
    this.loadingInitial = true;
    try {
      const payrollcycles = await agent.PayrollCycles.list();
      runInAction("loading payroll cycles", () => {
        payrollcycles.forEach((payrollcycle) => {
          this.payrollcycleRegistry.set(
              payrollcycle.endYear +
              "-" +
              payrollcycle.payRun,
            payrollcycle
          );
        });
      });
    } catch (error) {
      runInAction("load payroll cycles error", () => {
        this.loadingInitial = false;
      });
      toast.error("load payroll cycles error");
    }
  };

  @action loadTimeSheets = async () => {
    let initialPayrun = '';
    try {
      const timesheets = await agent.TimeSheetPays.list();
      runInAction("loading time sheets", () => {
        this.timesheetallRegistry.clear();
        timesheets.forEach((timesheet) => {
          timesheet.attendanceDate = timesheet.attendanceDate.substr(0, 10);
          this.timesheetallRegistry.set(timesheet.id, timesheet);
          this.payrunRegistry.set(timesheet.financeYear+timesheet.payRun, timesheet);
        });
        this.loadingInitial = false;
      });
      const sortPayrun = Array.from(this.payrunRegistry.values()).sort(
        (b, a) => a.financeYear - b.financeYear || a.payRun.localeCompare(b.payRun));
      if (sortPayrun.length > 0) initialPayrun = sortPayrun[0].financeYear + '-' + sortPayrun[0].payRun;
      this.selectPayRun(initialPayrun);
    } catch (error) {
      runInAction("load time sheets Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load time sheets Error");
    }
    return initialPayrun;
  };

  @action editTimeSheets = async (payrun: string, email: string, status: string) => {
    this.submitting = true;
    try {
      await agent.TimeSheetPays.update(payrun, email, status);
      runInAction("editing employee time sheet", () => {
        this.timesheetemployeeRegistry.forEach((timesheet) => {
            timesheet.status = status;
        });
        this.employeestatusRegistry.set(email, status);
        this.submitting = false;
        toast.success('Time Sheet '+status);
      });
    } catch (error) {
      runInAction("edit Time Sheet error", () => {
        this.submitting = false;
      });
      toast.error("edit Time Sheet error");
      console.log(error);
    }
  };

  @action selectPayRun = async (payRunFinance: string) => {
    const year = payRunFinance.split('-')[0];
    const payRun = payRunFinance.split('-')[1];
    let timeSheets = Array.from(this.timesheetallRegistry.values());
    const selectedTimeSheets = timeSheets.filter((timeSheet) =>
        timeSheet.payRun === payRun && timeSheet.financeYear === year
    );
    this.employeepayrunRegistry.clear();
    this.timesheetpayrunRegistry.clear();
    this.employeestatusRegistry.clear();
    this.timesheetemployeeRegistry.clear();
    this.payrunweekRegistry.clear();
    selectedTimeSheets.forEach((timeSheet) => {
        const emp = this.employeeRegistry.get(timeSheet.employeeEmail);
        this.employeepayrunRegistry.set(timeSheet.employeeEmail, emp);
        this.employeestatusRegistry.set(timeSheet.employeeEmail, timeSheet.status);
        this.timesheetpayrunRegistry.set(timeSheet.id, timeSheet);
    });
  };

  @action selectEmployee = async (email: string) => {
    let timeSheets = Array.from(this.timesheetpayrunRegistry.values());
    const selectedTimeSheets = timeSheets.filter((timeSheet) =>
        timeSheet.employeeEmail === email
    ).sort(
      (a, b) => Date.parse(a.attendanceDate) - Date.parse(b.attendanceDate)
    );
    this.timesheetemployeeRegistry.clear();
    this.payrunweekRegistry.clear();
    selectedTimeSheets.forEach((timeSheet, index) => {
        if (index < 7) this.payrunweekRegistry.set(timeSheet.attendanceDate, 'week1')
        else this.payrunweekRegistry.set(timeSheet.attendanceDate, 'week2');
        if (timeSheet.attendant === 'Y') this.timesheetemployeeRegistry.set(timeSheet.id, timeSheet);
    });
  };

  @action loadEmployees = async () => {
    try {
      const employees = await agent.Employees.list();
      runInAction("loading empolyees", () => {
        this.employeeRegistry.clear();
        employees.forEach((employee) => {
          this.employeeRegistry.set(employee.email, employee);
        });
      });
    } catch (error) {
      runInAction("load Employee List Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load Employee List Error");
    }
  };

  @action loadProjectTimes = async () => {
    // this.loadingInitial = true;
    try {
      const projecttimes = await agent.ProjectTimes.list();
      runInAction('loading projecttimes', () => {
        projecttimes.forEach(projecttime => {
          this.projecttimeRegistry.set(projecttime.code, projecttime);
        });
        this.loadingInitial = false;
      })
    } catch (error) {
      runInAction('load Project Time List error' ,() => {
        this.loadingInitial = false;
      })
      toast.error('load Project Time List error');
    }
  };

  @action clearPage = () => {
    this.timesheetallRegistry.clear();
    this.timesheetpayrunRegistry.clear();
    this.timesheetemployeeRegistry.clear();
    this.payrunRegistry.clear();
    this.payrollcycleRegistry.clear();
    this.employeeRegistry.clear();
    this.employeepayrunRegistry.clear();
    this.employeestatusRegistry.clear();
    this.payrunweekRegistry.clear();
  }

}

export default createContext(new TimeSheetPayStore());
